import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Minus, Plus, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { API, fmt, imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import axios from "axios";

const HeatMeter = ({ level = 0 }) => (
  <div className="flex items-center gap-0.5" title={`Heat level ${level}/5`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Flame
        key={i}
        className={`w-3.5 h-3.5 ${i < level ? "text-terra fill-terra" : "text-ink/15"}`}
      />
    ))}
  </div>
);

const ScoreBar = ({ label, score }) => {
  const value = parseFloat(score) || 0;
  if (!value) return null;
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-[10px] uppercase tracking-widest text-ink/50 w-14">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-parchdeep overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: `${(value / 10) * 100}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="h-full rounded-full bg-gold"
        />
      </div>
      <span className="font-mono text-[10px] text-ink/50">{value.toFixed(1)}</span>
    </div>
  );
};

const ProductCard = ({ product, index }) => {
  const { addToCart, setIsOpen } = useCart();
  const [qty, setQty] = useState(1);
  const [expanded, setExpanded] = useState(false);

  const handleAdd = () => {
    addToCart(product, qty);
    toast.success(`${product.name} added to your basket`, {
      description: `${qty} × ${fmt(product.price)} — ${product.size}`,
      action: { label: "View Basket", onClick: () => setIsOpen(true) },
    });
    setQty(1);
  };

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -4 }}
      className="group bg-white rounded-2xl border border-parchborder shadow-sm hover:shadow-xl transition-shadow duration-300 overflow-hidden flex flex-col"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative overflow-hidden aspect-square">
        <img
          src={imgUrl(product.image)}
          alt={product.name}
          loading="lazy"
          className={`w-full h-full object-cover transition-transform duration-700 ease-out ${
            product.sold_out ? "grayscale opacity-70" : "group-hover:scale-[1.04]"
          }`}
        />
        {product.badge && !product.sold_out && (
          <span className="absolute top-4 left-4 bg-dill text-parchment font-mono text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full">
            {product.badge}
          </span>
        )}
        {product.sold_out && (
          <div className="absolute inset-0 bg-ink/30 flex items-center justify-center">
            <span
              className="font-serif font-black text-terra bg-parchment/95 rounded-full px-5 py-2 text-sm uppercase tracking-[0.2em] -rotate-6 shadow-lg"
              data-testid={`product-soldout-${product.id}`}
            >
              Sold Out
            </span>
          </div>
        )}
        {product.coming_soon && !product.sold_out && (
          <span
            className="absolute top-4 right-4 bg-gold text-dill-deep font-mono text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full shadow-md"
            data-testid={`product-comingsoon-${product.id}`}
          >
            Coming Soon
          </span>
        )}
      </div>

      <div className="flex flex-col flex-1 p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra font-semibold mb-1.5">
          {product.flavor_category}
        </p>
        <h3 className="font-serif font-bold text-dill tracking-tight text-xl">
          {product.name}
        </h3>
        <p className="font-serif italic text-ink/50 text-sm mt-0.5">{product.tagline}</p>

        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="md:hidden mt-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-terra font-semibold"
          data-testid={`product-details-toggle-${product.id}`}
        >
          {expanded ? "Hide jar details" : "Jar details & scores"}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`} />
        </button>

        <div
          className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out md:block ${
            expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0 md:opacity-100"
          }`}
          data-testid={`product-details-${product.id}`}
        >
          <div className="overflow-hidden">
            <p className="text-sm text-ink/65 leading-relaxed mt-3">
              {product.description}
            </p>

            {(product.crunch_score || product.tang_score || product.sweet_score) && (
              <div className="mt-4 space-y-1.5">
                <ScoreBar label="Crunch" score={product.crunch_score} />
                <ScoreBar label="Tang" score={product.tang_score} />
                <ScoreBar label="Sweet" score={product.sweet_score} />
              </div>
            )}
          </div>
        </div>

        <div className="mt-auto">
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-parchborder">
            <div>
              <span className="font-serif font-black text-xl text-ink">{fmt(product.price)}</span>
              <span className="block font-mono text-[10px] uppercase tracking-widest text-ink/45 mt-0.5">
                {product.size}
              </span>
            </div>
            <HeatMeter level={product.heat_level} />
          </div>

          <div className="flex items-center gap-3 mt-4">
            <div className={`flex items-center border border-parchborder rounded-full ${product.sold_out || product.coming_soon ? "opacity-40 pointer-events-none" : ""}`}>
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="p-2 hover:text-terra transition-colors"
                aria-label="Decrease quantity"
                data-testid={`product-qty-dec-${product.id}`}
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-8 text-center font-mono text-sm font-semibold" data-testid={`product-qty-${product.id}`}>
                {qty}
              </span>
              <button
                onClick={() => setQty((q) => Math.min(product.stock ?? 50, q + 1))}
                className="p-2 hover:text-terra transition-colors"
                aria-label="Increase quantity"
                data-testid={`product-qty-inc-${product.id}`}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={handleAdd}
              disabled={product.sold_out || product.coming_soon}
              className={`flex-1 font-semibold rounded-full py-2.5 text-sm transition-colors duration-300 ${
                product.coming_soon && !product.sold_out
                  ? "bg-gold/15 text-gold border border-gold/40 cursor-not-allowed"
                  : "bg-dill hover:bg-terra disabled:bg-ink/25 disabled:hover:bg-ink/25 disabled:cursor-not-allowed text-parchment"
              }`}
              data-testid={`product-add-to-cart-${product.id}`}
            >
              {product.sold_out ? "Sold Out" : product.coming_soon ? "Coming Soon" : "Add to Basket"}
            </button>
          </div>

          {product.stock != null && !product.sold_out && !product.coming_soon ? (
            <p
              className={`font-mono text-[10px] uppercase tracking-widest mt-3 ${product.stock <= 5 ? "text-terra font-bold" : "text-gold"}`}
              data-testid={`product-stock-${product.id}`}
            >
              {product.stock <= 5 ? `Only ${product.stock} left!` : `${product.stock} left this batch`}
            </p>
          ) : (
            product.inventory_status && (
              <p className="font-mono text-[10px] uppercase tracking-widest text-gold mt-3" data-testid={`product-stock-${product.id}`}>
                {product.inventory_status}
              </p>
            )
          )}
        </div>
      </div>
    </motion.article>
  );
};

export const ProductCatalog = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((res) => setProducts(res.data))
      .catch(() => toast.error("Could not load the pantry. Please refresh."))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(
    () => ["All", ...new Set(products.map((p) => p.flavor_category).filter(Boolean))],
    [products]
  );

  const visible = useMemo(() => {
    return filter === "All" ? products : products.filter((p) => p.flavor_category === filter);
  }, [products, filter]);

  return (
    <section id="shop" className="py-12 sm:py-16 bg-parchdeep/50" data-testid="product-catalog">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12"
        >
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-terra font-semibold mb-4">
              The Pantry Shelf
            </p>
            <h2 className="font-serif font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-dill leading-tight">
              This season's jars.
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="category-filters">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`rounded-full px-4 py-2 text-xs font-mono uppercase tracking-widest transition-colors duration-300 border ${
                  filter === cat
                    ? "bg-dill text-parchment border-dill"
                    : "bg-transparent text-ink/60 border-parchborder hover:border-terra hover:text-terra"
                }`}
                data-testid={`filter-category-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-dill" data-testid="catalog-loading">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <AnimatePresence mode="popLayout">
              {visible.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </section>
  );
};
